import React, { useState, useEffect } from 'react';
import { X, Settings, ChevronDown, ChevronUp } from 'lucide-react';

interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
}

interface CookieSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    onConsentChange?: (preferences: CookiePreferences) => void;
}

const CookieSettings: React.FC<CookieSettingsProps> = ({ isOpen, onClose, onConsentChange }) => {
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true,
        analytics: false,
        marketing: false
    });
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        // Load current preferences when component opens
        if (isOpen) {
            const consent = localStorage.getItem('cookie-consent');
            if (consent) {
                try {
                    const savedPreferences = JSON.parse(consent);
                    setPreferences(savedPreferences);
                } catch {
                    // If parsing fails, use defaults
                }
            }
        }
    }, [isOpen]);

    const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
        if (type === 'necessary') return; // Can't disable necessary cookies
        setPreferences((prev) => ({
            ...prev,
            [type]: value
        }));
    };

    const handleSaveSettings = () => {
        localStorage.setItem('cookie-consent', JSON.stringify(preferences));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        onConsentChange?.(preferences);
        onClose();
    };

    const handleAcceptAll = () => {
        const newPreferences = {
            necessary: true,
            analytics: true,
            marketing: true
        };
        localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        setPreferences(newPreferences);
        onConsentChange?.(newPreferences);
        onClose();
    };

    const handleRejectAll = () => {
        const newPreferences = {
            necessary: true,
            analytics: false,
            marketing: false
        };
        localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        setPreferences(newPreferences);
        onConsentChange?.(newPreferences);
        onClose();
    };

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const cookieDetails: {
        necessary: Array<{ name: string; purpose: string; expiry: string; type: string }>;
        analytics: Array<{ name: string; purpose: string; expiry: string; type: string }>;
        marketing: Array<{ name: string; purpose: string; expiry: string; type: string }>;
    } = {
        necessary: [
            {
                name: 'cookie-consent',
                purpose: 'Stores your cookie preferences and consent choices',
                expiry: 'Persistent',
                type: 'localStorage'
            },
            {
                name: 'cookie-consent-date',
                purpose: 'Records when you gave consent for compliance tracking',
                expiry: 'Persistent',
                type: 'localStorage'
            }
        ],
        analytics: [
            {
                name: '_ga',
                purpose: 'Distinguishes users for Google Analytics',
                expiry: '2 years',
                type: 'Cookie'
            },
            {
                name: '_ga_*',
                purpose: 'Contains campaign and traffic source information',
                expiry: '2 years',
                type: 'Cookie'
            },
            {
                name: '_gid',
                purpose: 'Distinguishes users for Google Analytics',
                expiry: '24 hours',
                type: 'Cookie'
            }
        ],
        marketing: []
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl border max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Settings className="h-5 w-5 text-primary-600" />
                            <h3 className="text-lg font-semibold text-gray-900">Cookie Settings</h3>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <p className="text-sm text-gray-600 mb-6">
                        Manage your cookie preferences. You can change these settings at any time.
                    </p>

                    <div className="space-y-4 mb-6">
                        {/* Necessary Cookies */}
                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 mr-4">
                                    <h4 className="font-medium text-gray-900">Necessary Cookies</h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Required for the website to function properly. Cannot be
                                        disabled.
                                    </p>
                                    <button
                                        onClick={() => toggleSection('necessary')}
                                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        {expandedSections.necessary ? (
                                            <ChevronUp className="h-4 w-4" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4" />
                                        )}
                                        View cookies ({cookieDetails.necessary.length})
                                    </button>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        disabled
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 opacity-50"
                                    />
                                </div>
                            </div>
                            {expandedSections.necessary && (
                                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                    {cookieDetails.necessary.map((cookie, index) => (
                                        <div
                                            key={index}
                                            className="text-xs bg-gray-50 p-2 rounded-sm"
                                        >
                                            <div className="font-medium text-gray-700">
                                                {cookie.name}
                                            </div>
                                            <div className="text-gray-600">{cookie.purpose}</div>
                                            <div className="text-gray-500">
                                                Type: {cookie.type} • Expiry: {cookie.expiry}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Analytics Cookies */}
                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 mr-4">
                                    <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Help us understand how visitors interact with our website
                                        through Google Analytics.
                                    </p>
                                    <button
                                        onClick={() => toggleSection('analytics')}
                                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        {expandedSections.analytics ? (
                                            <ChevronUp className="h-4 w-4" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4" />
                                        )}
                                        View cookies ({cookieDetails.analytics.length})
                                    </button>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={preferences.analytics}
                                        onChange={(e) =>
                                            handlePreferenceChange('analytics', e.target.checked)
                                        }
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                            {expandedSections.analytics && (
                                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                    {cookieDetails.analytics.map((cookie, index) => (
                                        <div
                                            key={index}
                                            className="text-xs bg-gray-50 p-2 rounded-sm"
                                        >
                                            <div className="font-medium text-gray-700">
                                                {cookie.name}
                                            </div>
                                            <div className="text-gray-600">{cookie.purpose}</div>
                                            <div className="text-gray-500">
                                                Type: {cookie.type} • Expiry: {cookie.expiry}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Marketing Cookies */}
                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 mr-4">
                                    <h4 className="font-medium text-gray-900">Marketing Cookies</h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Used to deliver personalized advertisements and track their
                                        effectiveness.
                                    </p>
                                    <button
                                        onClick={() => toggleSection('marketing')}
                                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        {expandedSections.marketing ? (
                                            <ChevronUp className="h-4 w-4" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4" />
                                        )}
                                        View cookies ({cookieDetails.marketing.length})
                                    </button>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={preferences.marketing}
                                        onChange={(e) =>
                                            handlePreferenceChange('marketing', e.target.checked)
                                        }
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                            {expandedSections.marketing && (
                                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                    {cookieDetails.marketing.length === 0 ? (
                                        <div className="text-xs bg-gray-50 p-3 rounded-sm text-center text-gray-600">
                                            No marketing cookies are currently used on this website.
                                        </div>
                                    ) : (
                                        cookieDetails.marketing.map((cookie, index) => (
                                            <div
                                                key={index}
                                                className="text-xs bg-gray-50 p-2 rounded-sm"
                                            >
                                                <div className="font-medium text-gray-700">
                                                    {cookie.name}
                                                </div>
                                                <div className="text-gray-600">
                                                    {cookie.purpose}
                                                </div>
                                                <div className="text-gray-500">
                                                    Type: {cookie.type} • Expiry: {cookie.expiry}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={handleSaveSettings}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                        >
                            Save Preferences
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={handleAcceptAll}
                                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
                            >
                                Accept All
                            </button>
                            <button
                                onClick={handleRejectAll}
                                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
                            >
                                Reject All
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieSettings;
